if (!customElements.get('shop-by-purpose-carousel')) {
  customElements.define(
    'shop-by-purpose-carousel',
    class ShopByPurposeCarousel extends HTMLElement {
      connectedCallback() {
        if (this.carouselReady) return;
        this.carouselReady = true;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.delay = Math.max(2000, Number(this.dataset.delay) || 3000);
        this.timer = null;
        this.settleTimer = null;
        this.moveTimer = null;
        this.resizeTimer = null;
        this.animating = false;
        this.pointerActive = false;
        this.originals = [];

        this.handlePointerDown = () => {
          this.pointerActive = true;
          this.stop();
          clearTimeout(this.moveTimer);
          this.scrollTo({ left: this.scrollLeft, behavior: 'auto' });
          this.animating = false;
          this.style.scrollSnapType = '';
        };
        this.handlePointerUp = () => {
          if (!this.pointerActive) return;
          this.pointerActive = false;
          this.queueSettle();
          this.schedule();
        };
        this.handleWheel = () => { this.stop(); this.queueSettle(); this.schedule(); };
        this.handleScroll = () => { if (!this.animating) this.queueSettle(); };
        this.handleFocusIn = () => this.stop();
        this.handleFocusOut = (event) => { if (!this.contains(event.relatedTarget)) this.schedule(); };
        this.handleVisibility = () => document.hidden ? this.stop() : this.schedule();
        this.handleResize = () => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = window.setTimeout(() => this.configure(), 160);
        };

        this.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        window.addEventListener('pointerup', this.handlePointerUp, { passive: true });
        window.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
        this.addEventListener('wheel', this.handleWheel, { passive: true });
        this.addEventListener('scroll', this.handleScroll, { passive: true });
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('visibilitychange', this.handleVisibility);
        window.addEventListener('resize', this.handleResize, { passive: true });
        this.mobileQuery.addEventListener?.('change', this.handleResize);
        this.configure();
      }

      disconnectedCallback() {
        this.clearTimers();
        this.removeClones();
        this.removeEventListener('pointerdown', this.handlePointerDown);
        window.removeEventListener('pointerup', this.handlePointerUp);
        window.removeEventListener('pointercancel', this.handlePointerUp);
        this.removeEventListener('wheel', this.handleWheel);
        this.removeEventListener('scroll', this.handleScroll);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        document.removeEventListener('visibilitychange', this.handleVisibility);
        window.removeEventListener('resize', this.handleResize);
        this.mobileQuery?.removeEventListener?.('change', this.handleResize);
        this.carouselReady = false;
      }

      clearTimers() {
        this.stop();
        clearTimeout(this.settleTimer);
        clearTimeout(this.moveTimer);
        clearTimeout(this.resizeTimer);
      }

      configure() {
        this.clearTimers();
        this.removeClones();
        this.originals = Array.from(this.children);
        if (!this.mobileQuery.matches || this.originals.length < 2) {
          this.scrollLeft = 0;
          return;
        }
        this.prepend(this.makeClone(this.originals[this.originals.length - 1]));
        this.append(this.makeClone(this.originals[0]));
        requestAnimationFrame(() => { this.goTo(1, false); this.schedule(); });
      }

      makeClone(slide) {
        const clone = slide.cloneNode(true);
        clone.dataset.purposeCarouselClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
        return clone;
      }

      removeClones() {
        this.querySelectorAll(':scope > [data-purpose-carousel-clone="true"]').forEach((clone) => clone.remove());
      }

      get slides() { return Array.from(this.children); }

      targetLeft(slide) {
        const viewportRect = this.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const slideLeft = this.scrollLeft + slideRect.left - viewportRect.left;
        return Math.max(0, slideLeft - ((this.clientWidth - slideRect.width) / 2));
      }

      nearestIndex() {
        let nearest = 0;
        let distance = Infinity;
        this.slides.forEach((slide, index) => {
          const currentDistance = Math.abs(this.targetLeft(slide) - this.scrollLeft);
          if (currentDistance < distance) { nearest = index; distance = currentDistance; }
        });
        return nearest;
      }

      goTo(index, smooth = true) {
        const slide = this.slides[index];
        if (!slide) return;
        this.animating = smooth;
        this.style.scrollSnapType = 'none';
        this.scrollTo({ left: this.targetLeft(slide), behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'auto' });
        clearTimeout(this.moveTimer);
        this.moveTimer = window.setTimeout(() => {
          this.animating = false;
          this.style.scrollSnapType = '';
          this.normalize(index);
        }, smooth && !this.reducedMotion.matches ? 520 : 20);
      }

      normalize(index = this.nearestIndex()) {
        const count = this.originals.length;
        if (!this.mobileQuery.matches || count < 2) return;
        if (index === 0) this.goTo(count, false);
        if (index === count + 1) this.goTo(1, false);
      }

      queueSettle() {
        clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(() => {
          if (!this.pointerActive && !this.animating) this.goTo(this.nearestIndex(), false);
        }, 160);
      }

      advance() {
        if (!this.mobileQuery.matches || this.pointerActive || this.animating) { this.schedule(); return; }
        let index = this.nearestIndex();
        if (index === 0) index = this.originals.length;
        this.goTo(index + 1);
        this.schedule();
      }

      schedule(wait = this.delay) {
        this.stop();
        if (!this.mobileQuery.matches || this.reducedMotion.matches || document.hidden || this.pointerActive || this.contains(document.activeElement) || this.originals.length < 2) return;
        this.timer = window.setTimeout(() => this.advance(), wait);
      }

      stop() { clearTimeout(this.timer); this.timer = null; }
    }
  );
}
